import React from 'react';
import { ProductHandle } from '../../enums/ProductHandle.enum';
import { ViewProduct } from '../../models/ViewProduct.model';
import { SizingToolState } from '../../models/actionStates';
import SelectedRadio from './SelectedRadio';
import { linkImage } from './style/StyleSelector';
import { useRecoilState, useRecoilValue } from 'recoil';
import { assetPathFnState } from '../../recoil/atoms';
import { productsState, sizingToolState } from '../../recoil/selectors';
import { productTitlePart1, productTitlePart2 } from '../../util';
import { useProductSelection } from '../../hooks/useProductSelection';
import styles from './FindYourSizeSelector.module.scss';

interface Props {
  title?: string;
  copy?: string;
  callback?: (product: ViewProduct) => void;
  selectedHandle?: ProductHandle;
  showCheckMarkOnSelectedProduct?: boolean;
}

export const FindYourSizeSelector: React.FC<Props> = ({
  callback,
  showCheckMarkOnSelectedProduct = true,
  selectedHandle = null,
  copy = 'Pick a product to get started.',
  title = '',
}) => {
  const products = useRecoilValue(productsState);
  const assetPathFn = useRecoilValue(assetPathFnState);
  const [sizingToolUIState, setSizingToolState] = useRecoilState(sizingToolState);
  const { setHandle } = useProductSelection(sizingToolUIState.selectionType, sizingToolUIState.selectionKey);

  const selectProduct = (product: ViewProduct) => {
    setHandle(product.handle);
    setSizingToolState({
      sizingToolState: SizingToolState.Open,
      skipTracking: true,
      selectionType: sizingToolUIState.selectionType,
      selectionKey: sizingToolUIState.selectionKey,
      product,
      initialProduct: product,
    });
    callback?.(product);
  };

  interface FindYourSizeProductIconProps {
    product: ViewProduct;
    imageSrc: string;
  }

  const FindYourSizeProductIcon: React.FC<FindYourSizeProductIconProps> = ({ product, imageSrc }) => {
    const productLineTitle = productTitlePart1(product.title);
    const productTitle = productTitlePart2(product.title);
    return (
      <div>
        <button className={styles.sizingProductSelectImageContainer} onClick={() => selectProduct(product)}>
          {showCheckMarkOnSelectedProduct && selectedHandle !== undefined && <SelectedRadio active={product.handle === selectedHandle} />}
          <img src={imageSrc} alt="product icon" />
          <div className={styles.sizingProductImageTitle}>
            <i>{productLineTitle}</i>
            <br />
            <span className={styles.sizingProductImageProductName}>{productTitle}</span>
          </div>
        </button>
      </div>
    );
  };

  const FindYourSizeProductSelector = () => {
    return (
      <React.Fragment>
        {[...Object.values(products)]
          .filter((p) => !p.hiddenInMenus)
          .map((product: ViewProduct, i) => {
            const imageSrc = linkImage(product, false, assetPathFn);

            if (!imageSrc) {
              return <React.Fragment key={i} />;
            }

            return <FindYourSizeProductIcon key={i} product={product} imageSrc={imageSrc} />;
          })}
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <div className={styles.findYourSizeContainer}>
        <div className={styles.productSelectorsWrapper}>
          <div className={styles.textContainer}>
            <span className={styles.titleText}>{title}</span>
            <span className={styles.subText}>{copy}</span>
          </div>
          <div className={styles.sizingProductIconContainer}>{<FindYourSizeProductSelector />}</div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default FindYourSizeSelector;
